import u from '@/utils'
import Promise from 'bluebird'
import axios from 'axios'

const state = {
  loadingBots: false,
  bots: [],
  botsWithProjection: [],
  botProfiles: {},
  botsTemplate: {},
  botIdentities: {},
  botHive: {},
  botWorkflows: {}
}
const getters = {
  getBots() {
    return state.bots
  },
  getBotsWithProjection() {
    return state.botsWithProjection
  }
}

const mutations = {
  setBotsLoading(state, isLoading) {
    state.loadingBots = isLoading
  },
  setBots(state, bots) {
    state.bots = bots
  },
  setBotsWithProjection(state, bots) {
    state.botsWithProjection = bots
  },

  updateBot(state, bot) {
    let index = state.bots.findIndex(item => item.hexId === bot.hexId)
    state.bots.splice(index, 1, bot)
  },

  setBotTemplate(state, payload) {
    state.botsTemplate = {
      ...state.botsTemplate,
      [payload.bot.hexId]: payload.botTemplate
    }
  },

  setBotProfiles(state, payload) {
    state.botProfiles = {
      ...state.botProfiles,
      [payload.bot.hexId]: payload.profiles
    }
  },
  setBotWorkflows(state, payload) {
    state.botWorkflows = {
      ...state.botWorkflows,
      [payload.bot.hexId]: payload.workflows
    }
  },

  setBotIdentities(state, payload) {
    state.botIdentities = {
      ...state.botIdentities,
      [payload.bot.hexId]: payload.identities
    }
  },

  setBotHive(state, payload) {
    state.botHive = {
      ...state.botHive,
      [payload.bot.hexId]: payload.hive
    }
  }
}

const actions = {
  async fetchBots({ commit }) {
    commit('setBotsLoading', true)
    let response = await axios.get('/api/v1/bots')
    let bots = [...response.data._embedded.bots]
    bots = bots.map(bot => u.processResp(bot))
    commit('setBots', bots)
    commit('setBotsLoading', false)
    return bots
  },

  async fetchBotSelfService({ commit }, hexId) {
    let response = await axios.get(`/api/v1/bots/${hexId}/selfService`)
    return response
  },

  async fetchBotsWithProjection({ commit }) {
    let response = await axios.get('/api/v1/bots?projection=withHiveAndMetrics')
    let bots = [...response.data._embedded.bots]
    bots = bots.map(bot => u.processResp(bot))
    commit('setBotsWithProjection', bots)
    return bots
  },

  async fetchBotProfiles({ commit }, bot) {
    let response = await axios.get(`/api/v1/bots/${bot.hexId}/profiles`)
    let profiles = [...response.data._embedded.profiles]
    profiles = profiles.map(profile => u.processResp(profile))
    commit('setBotProfiles', { bot, profiles })
    return profiles
  },

  async fetchBotWorkflows({ commit }, bot) {
    let response = await axios.get(`/api/v1/bots/${bot.hexId}/workflows`)
    let workflows = [...response.data._embedded.workflows]
    workflows = workflows.map(workflow => u.processResp(workflow))
    commit('setBotWorkflows', { bot, workflows })
    return workflows
  },

  async fetchBotIdentities({ commit }, bot) {
    let response = await axios.get(`/api/v1/bots/${bot.hexId}/identities`)
    let identities = [...response.data._embedded.identities]
    identities = identities.map(identity => u.processResp(identity))
    commit('setBotIdentities', { bot, identities })
    return identities
  },

  async fetchBotHive({ commit }, bot) {
    let response = await axios.get(`/api/v1/bots/${bot.hexId}/hive`)
    let hive = u.processResp(response.data)
    commit('setBotHive', { bot, hive })
    return hive
  },

  async deleteBot(context, botHexId) {
    await axios.delete(`/api/v1/bots/${botHexId}`)
  },

  async updateBot({ commit }, { body, hexId }) {
    let response = await axios.patch(`/api/v1/bots/${hexId}`, body)
    response.data = u.processResp(response.data)
    commit('updateBot', response.data)
    return response.data
  },

  async fetchBotTemplate({ commit }, bot) {
    let response = await axios.get(`/api/v1/bots/${bot.hexId}/botTemplate`)
    let botTemplate = [response.data]
    commit('setBotTemplate', { bot, botTemplate })
    return botTemplate
  },

  async setBotActive({ commit }, bot) {
    let body = {
      active: bot.active
    }
    let response = await axios.patch(`/api/v1/bots/${bot.hexId}`, body)
    commit('updateBot', response.data)
    return response.data
  },

  async setHiveToBot(context, payload) {
    let data = `/api/v1/hives/${payload.hiveId}`
    let response = await axios.put(
      `/api/v1/bots/${payload.bot.hexId}/hive`,
      data,
      {
        headers: {
          'Content-Type': 'text/uri-list'
        }
      }
    )
    return response.data
  },

  async addProfilesToBot(context, payload) {
    let data = payload.profileIds
      .map(profileId => {
        return `/api/v1/profiles/${profileId}`
      })
      .join('\n')
    let response = await axios.put(
      `/api/v1/bots/${payload.bot.hexId}/profiles`,
      data,
      {
        headers: {
          'Content-Type': 'text/uri-list'
        }
      }
    )
    return response.data
  },

  async addIdentitiesToBot(context, payload) {
    let data = payload.identities
      .map(identity => {
        return `/api/v1/identites/${identity.hexId}`
      })
      .join('\n')
    let response = await axios.post(
      `/api/v1/bots/${payload.bot.hexId}/identities`,
      data,
      {
        headers: {
          'Content-Type': 'text/uri-list'
        }
      }
    )
    return response.data
  },

  async deleteIdentitiesFromBot(context, payload) {
    return Promise.all(
      payload.identities.map(identity => {
        return axios.delete(
          `/api/v1/bots/${payload.bot.hexId}/identities/${identity.hexId}`
        )
      })
    )
  },

  async retriggerBot(context, hexId) {
    let response = await axios.post(`/api/v1/bots/${hexId}/retrigger`)
    return response.data
  },

  async queueBot(context, { body, hexId }) {
    let response = await axios.post(`/api/v1/bots/${hexId}/queue`, body)
    return response.data
  }
}

export default {
  state,
  actions,
  getters,
  mutations
}
